// src/component/AdditionalFieldsPopup.js

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {
    FaTimes, FaSpinner, FaExclamationTriangle, FaCheck,
    FaInfoCircle, FaUser, FaFileAlt, FaHashtag // Icons for info display
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import apiClient from '../api/apiClient'; // Ensure this points to your configured axios instance

// --- Helper to Render Dynamic Form Fields ---
const RenderFormField = ({ fieldDef, register, errors, styles }) => {
    const fieldName = fieldDef.fieldName;
    const label = fieldDef.label;
    const isRequired = fieldDef.required;
    const placeholder = fieldDef.placeholder || '';
    const inputType = fieldDef.type || 'text'; // Default to text

    // Basic validation rules
    const validationRules = { required: isRequired ? `${label} is required.` : false };
    if (inputType === 'number') {
        validationRules.valueAsNumber = true; // Ensure value is treated as a number
        if (fieldDef.min !== undefined) validationRules.min = { value: fieldDef.min, message: `${label} must be at least ${fieldDef.min}.` };
        if (fieldDef.max !== undefined) validationRules.max = { value: fieldDef.max, message: `${label} must be no more than ${fieldDef.max}.` };
    }
    if (inputType === 'email') {
        validationRules.pattern = { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address" };
    }
    // Add more specific patterns (e.g., for phone numbers) if needed based on fieldName or a custom type

    const commonProps = {
        id: fieldName,
        ...register(fieldName, validationRules), // Register the field with react-hook-form
        'aria-invalid': errors[fieldName] ? 'true' : 'false',
        style: styles.formInput // Apply consistent input styling
    };

    return (
        <div key={fieldName} style={styles.formGroup}>
            <label htmlFor={fieldName} style={styles.formLabel}>
                {label} {isRequired && <span style={styles.requiredMarker}>*</span>}
            </label>
            {inputType === 'textarea' ? (
                <textarea
                    {...commonProps}
                    placeholder={placeholder}
                    rows={fieldDef.rows || 3}
                    style={{...styles.formInput, ...styles.formTextarea}} // Apply specific textarea styles
                />
            ) : inputType === 'select' ? (
                <select {...commonProps} style={{...styles.formInput, ...styles.formSelect}}>
                    <option value="">-- Select {label} --</option>
                    {fieldDef.options?.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            ) : inputType === 'date' ? (
                 <input
                    type="date"
                    placeholder={placeholder}
                    {...commonProps}
                    // Ensure date inputs handle potential null/undefined values gracefully if needed
                 />
            ) : ( // Default to other input types (text, number, email, tel etc.)
                <input
                    type={inputType}
                    placeholder={placeholder}
                    {...commonProps}
                    // Specific attributes for number if needed
                    {...(inputType === 'number' && { step: fieldDef.step || 'any' })}
                />
            )}
            {/* Display validation error for the field */}
            {errors[fieldName] && (
                 <p style={styles.errorMessage} role="alert">
                     <FaExclamationTriangle size={12} style={{ marginRight: '4px'}} />
                     {errors[fieldName].message}
                 </p>
            )}
        </div>
    );
};

RenderFormField.propTypes = {
    fieldDef: PropTypes.object.isRequired,
    register: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    styles: PropTypes.object.isRequired,
};


// --- Main Popup Component ---
const AdditionalFieldsPopup = ({
    isOpen,
    onClose,
    orderItemId,
    fieldsDefinition,
    serviceName,
    applicantName,
    applicantId
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [submitError, setSubmitError] = useState(null);

    // react-hook-form setup
    const { register, handleSubmit, reset, formState: { errors, isDirty, isValid } } = useForm({
        mode: 'onChange', // Validate on change for better UX
        defaultValues: {}, // Start empty, will be populated by fetch
    });

    // --- Fetch Existing Data Effect ---
    useEffect(() => {
        if (isOpen && orderItemId && fieldsDefinition?.length > 0) {
            setIsLoadingData(true);
            setFetchError(null);
            setSubmitError(null);
            console.log(`[FieldsPopup] Fetching existing data for Order Item ID: ${orderItemId}`);

            // Use the /AdditionalFields route as requested
            const fetchUrl = `/order-items/${orderItemId}/AdditionalFields`;

            apiClient.get(fetchUrl)
                .then(response => {
                    console.log(`[FieldsPopup] Existing data fetched:`, response.data);
                    // Reset form with fetched data or empty object if data is null/undefined
                    reset(response.data || {});
                    setFetchError(null);
                })
                .catch(err => {
                    if (err.response && err.response.status === 404) {
                        console.log(`[FieldsPopup] No existing data found (404) at ${fetchUrl}. Initializing empty form.`);
                        // Initialize with empty defaults
                        const emptyDefaults = fieldsDefinition.reduce((acc, field) => {
                           acc[field.fieldName] = field.defaultValue !== undefined ? field.defaultValue : ''; // Use defaultValue from definition if provided
                           return acc;
                        }, {});
                        reset(emptyDefaults);
                        setFetchError(null);
                    } else {
                        console.error(`[FieldsPopup] Error fetching existing data from ${fetchUrl}:`, err);
                        const errorMsg = err.response?.data?.error || "Could not load saved data.";
                        setFetchError(errorMsg);
                        toast.error(`Error loading data: ${errorMsg}`);
                        reset({}); // Reset to empty on error
                    }
                })
                .finally(() => {
                    setIsLoadingData(false);
                });
        }

        // Cleanup: Reset form state when popup closes
        return () => {
            if (!isOpen) {
                 reset({}); // Clear form data
                 setIsLoadingData(false);
                 setFetchError(null);
                 setSubmitError(null);
                 setIsSubmitting(false);
            }
        };
    }, [isOpen, orderItemId, fieldsDefinition, reset]); // Dependencies for fetching

    // --- Form Submission Handler ---
    const onSubmit = useCallback(async (formData) => {
        if (!orderItemId) {
            toast.error("Cannot submit data: Order Item ID is missing.");
            return;
        }
        console.log(`[FieldsPopup] Submitting data for Order Item ID: ${orderItemId}`, formData);
        setIsSubmitting(true);
        setSubmitError(null);

        // Use the /AdditionalFields route for POST as requested
        const submitUrl = `/order-items/${orderItemId}/AdditionalFields`;

        try {
            await apiClient.post(submitUrl, formData);
            toast.success("Details saved successfully!");
            onClose(true); // Signal success to parent
        } catch (err) {
            console.error(`[FieldsPopup] Error submitting data to ${submitUrl}:`, err);
            // Try to get specific validation errors from backend if available
            const validationErrors = err.response?.data?.errors; // Assuming backend might send { errors: {fieldName: 'message'} }
            let errorMsg = err.response?.data?.error || err.message || "Failed to save details.";

            if (validationErrors && typeof validationErrors === 'object') {
                 errorMsg = "Please correct the highlighted errors."; // Generic message if field errors are shown inline
                 // react-hook-form doesn't automatically map backend errors,
                 // but you could manually set errors using `setError` if needed,
                 // or just display a general error message like `submitError`.
                 setSubmitError(errorMsg + " Check fields above."); // Adjust message
            } else {
                 setSubmitError(errorMsg); // Display general error near submit button
            }

            toast.error(`Save failed: ${errorMsg}`);
            // Keep popup open on error
        } finally {
            setIsSubmitting(false);
        }
    }, [orderItemId, onClose]);

    // --- Render Logic ---
    if (!isOpen) return null;

    const handleModalClick = (e) => e.stopPropagation(); // Prevent closing on modal click
    const canSubmit = isDirty && isValid && !isSubmitting; // Form has changed, is valid, and not currently submitting

    return (
        <div style={styles.overlay} onClick={() => !isSubmitting && onClose(false)}>
            <div style={styles.modalContent} onClick={handleModalClick}>
                {/* Close Button */}
                <button style={styles.closeButton} onClick={() => !isSubmitting && onClose(false)} aria-label="Close Popup" disabled={isSubmitting}>
                    <FaTimes />
                </button>

                {/* Title */}
                <h2 style={styles.title}>
                     <FaInfoCircle style={{ marginRight: '12px', color: '#17a2b8' }} />
                     Additional Information Required
                 </h2>

                 {/* Context Info */}
                 <div style={styles.contextInfo}>
                     <span title="Service Name"><FaFileAlt size={13} style={styles.contextIcon}/> {serviceName || 'N/A'}</span>
                     <span title="Applicant"><FaUser size={13} style={styles.contextIcon}/> {applicantName || 'N/A'} {applicantId ? `(ID: ${applicantId})` : ''}</span>
                     <span title="Order Item ID"><FaHashtag size={13} style={styles.contextIcon}/> Item #{orderItemId || 'N/A'}</span>
                 </div>


                {/* Loading State */}
                {isLoadingData && (
                    <div style={styles.centeredMessage}>
                         <FaSpinner className="fa-spin fa-lg" style={{marginRight: '10px'}}/> Loading Details...
                     </div>
                )}

                {/* Fetch Error State */}
                {fetchError && !isLoadingData && (
                     <div style={{...styles.errorDisplay, marginBottom: '1rem'}}>
                          <FaExclamationTriangle style={{ marginRight: '8px' }}/> {fetchError}
                      </div>
                )}

                {/* Form Area */}
                {!isLoadingData && !fetchError && fieldsDefinition && fieldsDefinition.length > 0 && (
                    <form onSubmit={handleSubmit(onSubmit)} style={styles.formContainer} noValidate>
                        {/* Dynamically render form fields */}
                        {fieldsDefinition.map(fieldDef => (
                            <RenderFormField
                                key={fieldDef.fieldName}
                                fieldDef={fieldDef}
                                register={register}
                                errors={errors}
                                styles={styles}
                            />
                         ))}

                        {/* Submission Error Display */}
                        {submitError && (
                             <div style={{...styles.errorDisplay, marginTop: '15px', marginBottom: '0px'}}> {/* Adjusted margin */}
                                  <FaExclamationTriangle style={{ marginRight: '8px' }}/> {submitError}
                              </div>
                        )}

                        {/* Footer with Action Buttons */}
                        <div style={styles.footer}>
                             <button
                                 type="button"
                                 style={{ ...styles.button, ...styles.cancelButton }}
                                 onClick={() => onClose(false)}
                                 disabled={isSubmitting}
                             >
                                 Cancel
                             </button>
                             <button
                                 type="submit"
                                 style={{ ...styles.button, ...styles.confirmButton, opacity: (!canSubmit && !isSubmitting) ? 0.6 : 1 }}
                                 disabled={!canSubmit || isSubmitting}
                                 title={!isValid ? "Please fill all required fields correctly" : (!isDirty ? "No changes detected" : "Save details")}
                             >
                                 {isSubmitting ? <FaSpinner className="fa-spin" style={{ marginRight: '8px' }} /> : <FaCheck style={{ marginRight: '8px' }} />}
                                 {isSubmitting ? 'Saving...' : 'Save Details'}
                             </button>
                        </div>
                    </form>
                 )}

                 {/* State when no fields are defined */}
                 {!isLoadingData && (!fieldsDefinition || fieldsDefinition.length === 0) && (
                     <div style={styles.centeredMessage}>
                          <FaInfoCircle style={{ marginRight: '8px' }} />
                          No additional details are required for this service.
                      </div>
                      // Optionally add a button to just close/confirm completion
                 )}
            </div>
        </div>
    );
};

// --- PropTypes ---
AdditionalFieldsPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    orderItemId: PropTypes.number,
    fieldsDefinition: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string.isRequired, // text, textarea, select, number, date, email etc.
        label: PropTypes.string.isRequired,
        required: PropTypes.bool,
        fieldName: PropTypes.string.isRequired, // Key used in form data object
        placeholder: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.any })), // For type: 'select'
        defaultValue: PropTypes.any, // Optional default value
        // Add other potential props used by RenderFormField (min, max, rows, pattern etc.)
        min: PropTypes.number,
        max: PropTypes.number,
        rows: PropTypes.number,
        step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    serviceName: PropTypes.string,
    applicantName: PropTypes.string,
    applicantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// --- Default Props ---
AdditionalFieldsPopup.defaultProps = {
    fieldsDefinition: [],
    serviceName: 'N/A',
    applicantName: 'N/A',
    applicantId: null,
};


// --- Styles (Combine styles from UploadDocumentsPopup and add form-specific ones) ---
const styles = {
    // Overlay, Modal, Close Button, Title (borrow from UploadDocumentsPopup or define consistently)
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1060, padding: '20px' }, // Higher z-index if needed
    modalContent: { backgroundColor: 'white', padding: '30px 40px', borderRadius: '8px', boxShadow: '0 5px 15px rgba(0,0,0,0.2)', position: 'relative', width: '90%', maxWidth: '650px', // Adjust max-width as needed for form
      maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
    closeButton: { position: 'absolute', top: '15px', right: '20px', background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: '#6c757d', padding: '5px', lineHeight: 1 },
    title: { marginTop: 0, marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '15px', color: '#333', fontSize: '1.4em', display: 'flex', alignItems: 'center' }, // Slightly smaller title?

    // Context Info Bar
    contextInfo: {
        marginBottom: '20px',
        padding: '12px 15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '5px',
        border: '1px solid #dee2e6',
        fontSize: '0.9em',
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '15px',
        color: '#495057'
    },
    contextIcon: {
        marginRight: '6px',
        verticalAlign: 'middle', // Align icons better
        color: '#007bff', // Use a consistent icon color
    },

    // Form specific styles
    formContainer: { flexGrow: 1, overflowY: 'auto', padding: '5px 5px 15px 5px', // Add padding bottom
        margin: '0 -5px -15px -5px', // Adjust negative margin
        maxHeight: 'calc(90vh - 250px)' // Adjust max height based on title/footer/context info height
     },
    formGroup: { marginBottom: '1.2rem' }, // Increased spacing between fields
    formLabel: { display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#495057', fontSize: '0.95em' },
    formInput: {
        width: '100%',
        padding: '0.75rem 1rem', // Slightly more padding
        border: '1px solid #ced4da',
        borderRadius: '4px',
        fontSize: '1rem',
        boxSizing: 'border-box',
        transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out',
        '&:focus': { borderColor: '#80bdff', outline: '0', boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)' }
    },
    formTextarea: {
        minHeight: '80px', // Ensure textarea has some default height
        lineHeight: 1.5,
    },
    formSelect: {
        appearance: 'none', // Basic reset for custom styling potential
        backgroundImage: `url('data:image/svg+xml;utf8,<svg fill="%236c757d" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/><path d="M0 0h24v24H0z" fill="none"/></svg>')`, // Basic dropdown arrow
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 1rem center',
        backgroundSize: '1em',
        paddingRight: '3rem', // Make space for arrow
    },
    requiredMarker: { color: '#dc3545', marginLeft: '4px' },
    errorMessage: { color: '#dc3545', fontSize: '0.85em', marginTop: '0.35rem', display: 'flex', alignItems: 'center'},

    // Footer & Buttons (borrow from UploadDocumentsPopup)
    footer: { borderTop: '1px solid #ddd', paddingTop: '20px', marginTop: '25px', display: 'flex', justifyContent: 'flex-end', gap: '15px' },
    button: { padding: '10px 22px', borderRadius: '5px', border: 'none', cursor: 'pointer', fontWeight: '500', fontSize: '1em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.2s ease, background-color 0.2s ease', lineHeight: 1.5 }, // Base button
    confirmButton: { backgroundColor: '#28a745', color: 'white', '&:hover:not(:disabled)': { backgroundColor: '#218838' } },
    cancelButton: { backgroundColor: '#6c757d', color: 'white', '&:hover:not(:disabled)': { backgroundColor: '#5a6268' } },

    // Loading / Error / Info Messages (borrow from UploadDocumentsPopup)
    centeredMessage: { textAlign: 'center', color: '#6c757d', padding: '30px 15px', fontSize: '1.1em', display: 'flex', alignItems: 'center', justifyContent: 'center'},
    errorDisplay: { backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', padding: '10px 15px', borderRadius: '4px', display: 'flex', alignItems: 'center', margin: '15px 0' }, // Keep margin consistent
    // Add infoDisplay if needed for non-error messages
};


export default AdditionalFieldsPopup;